import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'
import { DateRangePicker } from '~/components/date-picker'
import { Layout, LayoutBody, LayoutHeader } from '~/components/layout'
import {
  ChannelsCommandList,
  CommandPopover,
  GroupsCommandList,
  TagsCommandList,
  UsersCommandList,
} from '~/features/command-lists'
import { useFilterAccess } from '~/features/filter-control'
import { useTimespanPreference, useTitle } from '~/hooks'
import { ActivityOverview } from '~/modules/analytics/service-quality/activity-overview'

import { FilterCollection } from '@hap/analytics'
import { Button } from '@hap/components'
import { Timespan } from '@hap/utils'

import { DurationFilterPopover } from '../-components/duration-filter-popover'
import { hasActiveFilters } from '../-components/filter-utils'
import {
  activityFilters,
  convertActivityFilters,
  resetActivityFilters,
} from './-components/filter-utils'

const searchParams = z.object({
  filters: activityFilters.default({}),
  timespan: Timespan.Info.optional(),
})

export const Route = createFileRoute('/_authenticated/$alias/_app/activity/')({
  component: RouteComponent,
  validateSearch: searchParams,
})

function RouteComponent() {
  const { filters, timespan: selectedTimespan } = Route.useSearch()
  const { timespan, setTimespanPreference } = useTimespanPreference({ selectedTimespan })
  const navigate = useNavigate({ from: Route.fullPath })
  const { isFilterAllowed } = useFilterAccess()
  const daterange = Timespan.toStaticTimespan(timespan)
  const analyticsFilters = convertActivityFilters(filters)
  useTitle('Activity')

  function setFilter(
    key: 'channelIds' | 'tagIds' | 'groupIds' | 'reportsToIds' | 'handledByIds',
    values: string[],
  ) {
    navigate({
      search: (previous) => ({
        ...previous,
        filters: {
          ...previous.filters,
          [key]: values.length ? { op: 'in' as const, values } : undefined,
        },
      }),
    })
  }

  function resetFilters() {
    navigate({
      search: (previous) => ({
        ...previous,
        filters: resetActivityFilters(previous.filters, isFilterAllowed),
      }),
    })
  }

  return (
    <Layout>
      <LayoutHeader
        title="Activity"
        subtitle="Conversation experience and service-quality signals"
      />
      <LayoutBody>
        <div className="mb-6 flex flex-wrap items-center gap-2">
          <DateRangePicker
            value={timespan}
            onValueChange={(value) => {
              setTimespanPreference(value)
              navigate({ search: (previous) => ({ ...previous, timespan: value }) })
            }}
          />
          {(isFilterAllowed('channelId') || !!filters.channelIds) && (
            <CommandPopover
              label="Channels"
              collection={FilterCollection.Channels}
              selected={filters.channelIds?.values ?? []}
              disabled={!isFilterAllowed('channelId')}
            >
              <ChannelsCommandList
                multiselect
                onSelect={(ids) => setFilter('channelIds', ids)}
              />
            </CommandPopover>
          )}
          {(isFilterAllowed('tagIds') || !!filters.tagIds) && (
            <CommandPopover
              label="Highlights"
              collection={FilterCollection.Tags}
              selected={filters.tagIds?.values ?? []}
              disabled={!isFilterAllowed('tagIds')}
            >
              <TagsCommandList multiselect onSelect={(ids) => setFilter('tagIds', ids)} />
            </CommandPopover>
          )}
          {(isFilterAllowed('groupIds') || !!filters.groupIds) && (
            <CommandPopover
              label="Groups"
              collection={FilterCollection.Groups}
              selected={filters.groupIds?.values ?? []}
              disabled={!isFilterAllowed('groupIds')}
            >
              <GroupsCommandList
                multiselect
                onSelect={(ids) => setFilter('groupIds', ids)}
              />
            </CommandPopover>
          )}
          {(isFilterAllowed('handledByIds') || !!filters.handledByIds) && (
            <CommandPopover
              label="Agents"
              collection={FilterCollection.Users}
              selected={filters.handledByIds?.values ?? []}
              disabled={!isFilterAllowed('handledByIds')}
            >
              <UsersCommandList
                multiselect
                onSelect={(ids) => setFilter('handledByIds', ids)}
              />
            </CommandPopover>
          )}
          {(isFilterAllowed('assignedSupervisorId') || !!filters.reportsToIds) && (
            <CommandPopover
              label="Reports To"
              collection={FilterCollection.Users}
              selected={filters.reportsToIds?.values ?? []}
              disabled={!isFilterAllowed('assignedSupervisorId')}
            >
              <UsersCommandList
                multiselect
                onSelect={(ids) => setFilter('reportsToIds', ids)}
                contextFilters={{ subordinates: { $exists: true } }}
              />
            </CommandPopover>
          )}
          {(isFilterAllowed('handleTime') || !!filters.handleTime) && (
            <DurationFilterPopover
              value={filters.handleTime}
              disabled={!isFilterAllowed('handleTime')}
              onChange={(value) =>
                navigate({
                  search: (previous) => ({
                    ...previous,
                    filters: { ...previous.filters, handleTime: value },
                  }),
                })
              }
            />
          )}
          {hasActiveFilters(filters) && (
            <Button variant="ghost" icon="arrow-counter-clockwise" onClick={resetFilters}>
              Reset filters
            </Button>
          )}
        </div>
        <ActivityOverview
          queryContext={{
            timespan: {
              start: daterange.start.toISOString(),
              end: daterange.end.toISOString(),
            },
            filters: analyticsFilters,
          }}
        />
      </LayoutBody>
    </Layout>
  )
}
